import { observer } from 'mobx-react-lite'
import {
  AiCreateArtworkModel,
  FollowStatus,
  VisibleType,
  getCompetitionProductDetail_itemModule_artwork,
  modelOptions,
  AiCreateArtworkPattern,
} from '~/graphqls/types'

import styles from './AdvancedInfoForImage.module.css'
import classNames from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { useStores } from '~/hooks/useStores'
import { MyLink } from '~/components/ui/MyLink'
import { MyIcon } from '~/components/ui/MyIcon'
import { toYMDHms } from '~/utils'
import { useShowModel } from '../ai/useShowModel'
import Lightbox, { Slide } from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'

interface Props {
  product: getCompetitionProductDetail_itemModule_artwork
}

function AdvancedInfoForImage({ product }: Props) {
  const [currentInfoIndex, setCurrentInfoIndex] = useState(0)
  const { store } = useStores()
  const { getSubModelName } = useShowModel()
  // 图片预览
  const [previewVisible, setPreviewVisible] = useState(false)
  const [isPreviewMask, setIsPreviewMask] = useState(false)

  /** 风格模型 */
  const isST = product.aiArtwork?.model === AiCreateArtworkModel.STYLE
  /** 神奇涂抹 */
  const isMask = product.aiArtwork?.pattern === AiCreateArtworkPattern.MASK

  /** 创作者自己 */
  const isSelf = useMemo(() => {
    return product.creator?.uid === store.userinfo?.base?.uid
  }, [product, store])

  /** 作品公开状态： 取反的方法 */
  const viewStatus = useMemo(() => {
    if (isSelf) {
      return null
    }
    const visibleType = product?.contestInfoExtra?.visibleType
    const voted = product?.artworkSocialInfo?.voted
    const followStatus = product?.creator?.social?.followStatus
    if (visibleType === VisibleType.NONE) {
      return '未公开'
    } else if (visibleType === VisibleType.ONLY_FANS) {
      if (
        followStatus === FollowStatus.NON ||
        followStatus === FollowStatus.HE_ONE_WAY
      ) {
        return '仅关注可见'
      }
    } else if (visibleType === VisibleType.ONLY_VOTED) {
      if (!voted) {
        return '仅投票可见'
      }
    }
    return null
  }, [isSelf, product])

  /** 处理作品信息字段 */
  const genInfos = (value?: string) => {
    if (!viewStatus) return value
    return viewStatus
  }
  const genImageUrl = useCallback(
    (isMask?: boolean) => {
      const imgUrls =
        product.aiArtwork?.multiReferenceImageUrlList
          ?.filter(item => !!item)
          .map(item => item!) ?? []

      if (!viewStatus) {
        if (isMask) {
          return !product.aiArtwork?.maskImageUrl ? null : (
            <span className="text-primary cursor-pointer">查看</span>
          )
        }
        return imgUrls.length == 0 ? null : (
          <span className="text-primary cursor-pointer">查看</span>
        )
      }
      return viewStatus
    },
    [product]
  )

  const createInfoItems = [
    {
      name: '创作时间',
      value: toYMDHms(product?.aiArtwork?.generateTime as number),
      isShow: true,
    },
    {
      name: '模型主题',
      value: genInfos(
        modelOptions?.filter(it => it.model === product?.aiArtwork?.model)?.[0]
          ?.name + getSubModelName(product?.aiArtwork)
      ),
      isShow: true,
    },
    {
      name: '标签选择',
      value:
        genInfos(
          product?.aiArtwork?.styleDecorationOptions
            ?.map(item => item.name)
            ?.join(',')
        ) || '无',
      isShow: !isST,
    },
    {
      name: '画面尺寸',
      value: genInfos(`${product?.aiArtwork?.size}` as string) || '无',
      isShow: true,
    },
    {
      name: '角色同人',
      value:
        genInfos(
          product?.aiArtwork?.characterOptions
            ?.map(item => item.name)
            ?.join(',')
        ) || '无',
      isShow: !isST,
    },
    {
      name: '融合模型',
      value:
        genInfos(
          product?.aiArtwork?.modelFusionOptions
            ?.map(item => `${item.name}(${item.weight})`)
            ?.join(' , ')
        ) || '无',
      isShow: !isST,
    },
    {
      name: '参考图',
      value: genImageUrl() || '无',
      action: () => {
        const imgUrls =
          product.aiArtwork?.multiReferenceImageUrlList
            ?.filter(item => !!item)
            .map(item => item!) ?? []
        if (imgUrls.length == 0 || viewStatus) return
        setPreviewVisible(true)
        setIsPreviewMask(false)
      },
      isShow: !isST,
    },
    {
      name: '原图',
      value: genImageUrl(true) || '无',
      action: () => {
        if (!product.aiArtwork?.maskImageUrl || viewStatus) return
        setPreviewVisible(true)
        setIsPreviewMask(true)
      },
      isShow: isMask,
    },
  ]

  const techInfoItems = [
    {
      icon: 'hash',
      name: '链上Hash',
      value: product?.chain?.hash || '上链中',
    },
    {
      icon: 'bank',
      name: '作品存证',
      value: (
        <MyLink
          href={`/evidence?id=${product?.chain?.certificateNo}`}
          exist={false}
        >
          <div className="flex items-center justify-end">
            <span>查看</span>
            <MyIcon icon="arrow-right-simple text-[24px]" />
          </div>
        </MyLink>
      ),
    },
    {
      icon: 'barcode',
      name: '机器编号',
      value: product?.aiArtwork?.technologyInfo?.serverId || '无',
    },
    {
      icon: 'vc',
      name: '显卡',
      value: product?.aiArtwork?.technologyInfo?.gpuType || '无',
    },
    {
      icon: 'electrical',
      name: '消耗电量',
      value: product?.aiArtwork?.technologyInfo?.electricity
        ? `${product?.aiArtwork?.technologyInfo?.electricity}度 `
        : '无',
    },
    {
      icon: 'pip',
      name: '分辨率',
      value: product?.aiArtwork?.specificSize || '无',
    },
  ]
  /** 预览图片 */
  const previewImages = useMemo((): Slide[] => {
    if (isPreviewMask) {
      return [{ src: product.aiArtwork?.maskImageUrl! }]
    }
    return (product.aiArtwork?.multiReferenceImageUrlList || [])
      ?.filter(item => !!item)
      .map(item => {
        return { src: item! }
      })
  }, [product, isPreviewMask])
  return (
    <>
      <div className={styles.tabs}>
        <div
          className={classNames(styles.tabItem, {
            active: currentInfoIndex === 0,
          })}
          onClick={() => setCurrentInfoIndex(0)}
        >
          创作信息
        </div>
        <div
          className={classNames(styles.tabItem, {
            active: currentInfoIndex === 1,
          })}
          onClick={() => setCurrentInfoIndex(1)}
        >
          技术信息
        </div>
      </div>
      {currentInfoIndex === 0 && (
        <>
          <div className="text-sm mb-3 text-neutral-500">画面描述</div>
          <div className="px-4 py-3 text-neutral-800 text-sm border border-neutral-300 rounded-xl mb-6 break-all">
            {genInfos(product?.aiArtwork?.prompt)}
          </div>
        </>
      )}
      {currentInfoIndex === 0 && (
        <div className={styles.infos}>
          {createInfoItems
            .filter(item => item.isShow)
            .map(it => (
              <div key={it.name}>
                <span>{it.name}</span>
                <span onClick={it?.action}>{it.value}</span>
              </div>
            ))}
        </div>
      )}
      {currentInfoIndex === 1 && (
        <div className={styles.infos}>
          {techInfoItems.map(it => (
            <div key={it.name}>
              <span>{it.name}</span>
              <span>{it.value}</span>
            </div>
          ))}
        </div>
      )}
      <Lightbox
        open={previewVisible}
        close={() => setPreviewVisible(false)}
        index={0}
        slides={previewImages}
        render={{
          iconPrev: () => (
            <div className="flex rounded-full w-10 h-10 border-white border-2 justify-center items-center">
              <MyIcon icon="arrow-left" className="text-lg" />
            </div>
          ),
          iconNext: () => (
            <div className="flex rounded-full w-10 h-10 border-white border-2 justify-center items-center">
              <MyIcon icon="arrow-right" className="text-lg" />
            </div>
          ),
        }}
        carousel={{
          finite: true,
          preload: 2,
          imageFit: 'contain',
        }}
        animation={{ fade: 250, swipe: 500 }}
      />
    </>
  )
}

export default observer(AdvancedInfoForImage)
